import type {
  FieldValues,
  Path,
  UseFormSetError,
  UseFormSetFocus,
} from 'react-hook-form';

import { ApiError, type ServerFieldError } from './envelope';

/**
 * Turns a refusal into messages on the fields it names.
 *
 * The backend answers a validation failure with one sentence joining every
 * problem ("Name is required. Website: Value error, must start with
 * https://"). Each problem names its field only by the last part of its
 * location, title-cased, so the sentence is parsed back apart here - and the
 * moment the backend sends the structured list proposed in this change's
 * design (`data.errors` on the envelope), that is used and nothing is parsed.
 *
 * Each form supplies its own map from server field name to form field name,
 * in page order, because the two do not match (the profile form is flat where
 * the server is nested) and only the form knows its own layout.
 */

export type { ServerFieldError } from './envelope';

/** "<Label>: <message>", the handler's shape for a field with a message. */
const LABELLED = /^([A-Z][A-Za-z ]*): (.*)$/s;

/** "<Label> is required", the handler's shape for a missing field. */
const REQUIRED = /^([A-Z][A-Za-z ]*) is required\.?$/;

/**
 * Where one problem ends and the next begins: ". " followed by what reads as
 * a label. A message's own full stop is not followed by one, so it does not
 * split - and when the guess is ever wrong, the pieces land in the form-level
 * message rather than being lost.
 */
const PROBLEM_BOUNDARY = /\. (?=[A-Z][A-Za-z ]*(?:: | is required))/;

/** "Contact Email" -> "contact_email". "Body" means the whole record. */
function labelToField(label: string): string {
  if (label === 'Body') return '';
  return label.toLowerCase().replace(/ /g, '_');
}

/** "primary_contact.email" -> "Primary Contact Email", for the remainder. */
function labelOf(field: string): string {
  return field
    .split(/[._]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Parse the current one-sentence format into field errors.
 *
 * A piece that reads as neither of the handler's shapes - including a whole
 * sentence in some other format - comes back as one whole-record message, so
 * whatever the server said is still shown somewhere.
 */
export function parseErrorSentence(sentence: string): ServerFieldError[] {
  const trimmed = sentence.trim();
  if (!trimmed) return [];

  return trimmed.split(PROBLEM_BOUNDARY).map((part) => {
    const required = REQUIRED.exec(part);
    if (required) {
      return { field: labelToField(required[1]), message: 'is required' };
    }
    const labelled = LABELLED.exec(part);
    if (labelled) {
      return {
        field: labelToField(labelled[1]),
        // Pydantic's own prefix on a custom validator's message; the words
        // after it are the ones written for people.
        message: labelled[2].replace(/^Value error, /, ''),
      };
    }
    return { field: '', message: part };
  });
}

/**
 * The field errors a refusal carries: the structured list when the server
 * sent one, else the sentence parsed - but only for a 422, because only a
 * validation failure's sentence follows the parsed format. Anything else
 * (a 403, a 500) is one message about the whole request, not about fields.
 */
export function fieldErrorsOf(error: ApiError): ServerFieldError[] {
  if (error.fieldErrors && error.fieldErrors.length > 0) return error.fieldErrors;
  if (error.status === 422 && error.apiError) return parseErrorSentence(error.apiError);
  return [];
}

/** The two things a form hands over so a refusal can be put on its fields. */
interface ServerErrorForm<T extends FieldValues> {
  setError: UseFormSetError<T>;
  setFocus: UseFormSetFocus<T>;
}

/**
 * Put a refusal where it belongs: each field message on its field (marked
 * `type: 'server'`, which the next edit clears), focus on the first refused
 * field in the map's order, and everything that concerns no visible field
 * returned as the remainder for the form-level alert. Nothing is dropped.
 *
 * A 409 with `conflictField` set lands there whole - the name clash and the
 * invite address conflict, exactly as they were hand-mapped before.
 */
export function applyServerErrors<T extends FieldValues>(
  error: unknown,
  form: ServerErrorForm<T>,
  map: Record<string, Path<T>>,
  options: { conflictField?: Path<T> } = {},
): { remainder: string | null } {
  if (!(error instanceof ApiError)) {
    return {
      remainder:
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.',
    };
  }

  if (error.status === 409 && options.conflictField) {
    form.setError(options.conflictField, {
      type: 'server',
      message: error.serverMessage ?? error.message,
    });
    form.setFocus(options.conflictField);
    return { remainder: null };
  }

  const fieldErrors = fieldErrorsOf(error);
  if (fieldErrors.length === 0) {
    // A refusal about the whole request; ApiError.message already falls back
    // to a readable sentence when the server said nothing quotable.
    return { remainder: error.message };
  }

  const marked = new Set<Path<T>>();
  const leftovers: string[] = [];
  for (const { field, message } of fieldErrors) {
    const formField = field ? map[field] : undefined;
    if (formField) {
      form.setError(formField, { type: 'server', message });
      marked.add(formField);
    } else {
      // A field the form has no control for is named rather than lost; a
      // whole-record message stands on its own.
      leftovers.push(field ? `${labelOf(field)}: ${message}` : message);
    }
  }

  // The first refused field in the map's order, which each form lists in page
  // order - not in whatever order the server mentioned them.
  for (const formField of Object.values(map)) {
    if (marked.has(formField)) {
      form.setFocus(formField);
      break;
    }
  }

  return { remainder: leftovers.length > 0 ? leftovers.join('. ') : null };
}
